import { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Button, Checkbox, FormControlLabel, Chip, Divider, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FolderIcon from '@mui/icons-material/Folder';
import PlaceIcon from '@mui/icons-material/Place';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { poisActions } from '../../store';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../../common/util/fetchOrThrow';
import DraggableFilterPanel from './DraggableFilterPanel';
import { WIDGET_POSITIONS } from '../../common/theme/layout';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const useStyles = makeStyles()((theme, { tokens }) => ({
  content: {
    padding: theme.spacing(0.5, 1),
  },
  groupItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.25, 0.5),
    minHeight: 32,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: tokens.surface.glassHover,
    },
  },
  groupLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    overflow: 'hidden',
  },
  groupName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem',
    color: tokens.text.primary,
  },
  countChip: {
    minWidth: 24,
    height: 18,
    fontSize: '0.65rem',
    fontWeight: 600,
    backgroundColor: tokens.surface.glass,
    color: tokens.text.secondary,
    border: `1px solid ${tokens.border.subtle}`,
    '& .MuiChip-label': {
      padding: '0 4px',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0),
  },
  actionButton: {
    fontSize: '0.65rem',
    padding: theme.spacing(0.25, 0.5),
    minWidth: 'auto',
    borderRadius: 8,
    color: tokens.text.secondary,
    '&:hover': {
      backgroundColor: tokens.accent.subtle,
      color: tokens.accent.primary,
    },
  },
  checkbox: {
    padding: theme.spacing(0.25),
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.accent.primary,
    },
  },
}));

const PoiFilterPanel = ({ open, onClose, initialPosition }) => {
  const tokens = usePremiumTokens();
  const { classes } = useStyles({ tokens });
  const dispatch = useDispatch();
  const t = useTranslation();

  const pois = useSelector((state) => state.pois.items);
  const groups = useSelector((state) => state.pois.groups);
  const groupFilters = useSelector((state) => state.pois.groupFilters);

  // Load groups if not loaded
  useEffectAsync(async () => {
    if (open && Object.keys(groups).length === 0) {
      try {
        const response = await fetchOrThrow('/api/poigroups');
        const data = await response.json();
        dispatch(poisActions.refreshGroups(data));
      } catch (error) {
        console.error('Error loading POI groups:', error);
      }
    }
  }, [open]);

  // Calculate POI counts per group
  const groupCounts = useMemo(() => {
    const counts = { ungrouped: 0 };
    Object.values(pois).forEach((poi) => {
      if (poi.groupId && poi.groupId > 0) {
        counts[poi.groupId] = (counts[poi.groupId] || 0) + 1;
      } else {
        counts.ungrouped += 1;
      }
    });
    return counts;
  }, [pois]);

  // Get total visible POIs
  const visibleCount = useMemo(() => {
    let count = 0;
    Object.values(pois).forEach((poi) => {
      const groupKey = poi.groupId && poi.groupId > 0 ? poi.groupId : 'ungrouped';
      if (groupFilters[groupKey] !== false) {
        count += 1;
      }
    });
    return count;
  }, [pois, groupFilters]);

  const totalCount = Object.keys(pois).length;

  // Check if group is visible
  const isGroupVisible = useCallback((groupId) => groupFilters[groupId] !== false, [groupFilters]);

  // Toggle group visibility
  const handleToggleGroup = useCallback((groupId) => {
    dispatch(poisActions.toggleGroupFilter(groupId));
  }, [dispatch]);

  // Show all groups
  const handleShowAll = useCallback(() => {
    dispatch(poisActions.showAllGroups());
  }, [dispatch]);

  // Hide all groups
  const handleHideAll = useCallback(() => {
    const allGroupIds = Object.keys(groups).map((id) => parseInt(id, 10));
    dispatch(poisActions.hideAllGroups(allGroupIds));
  }, [dispatch, groups]);

  // Sort groups by name
  const sortedGroups = useMemo(() => Object.values(groups).sort((a, b) => a.name.localeCompare(b.name)), [groups]);

  return (
    <DraggableFilterPanel
      panelId="poi-filter"
      widgetId="poiFilter"
      open={open}
      onClose={onClose}
      title={t('poiFilterGroups') || 'Filtrar POIs'}
      icon={<FilterListIcon fontSize="small" color="action" />}
      count={`${visibleCount}/${totalCount}`}
      countColor={visibleCount === totalCount ? 'primary' : 'warning'}
      initialPosition={initialPosition || { top: WIDGET_POSITIONS.poiFilter.top, right: WIDGET_POSITIONS.poiFilter.right }}
    >
      <Box className={classes.content}>
        {/* Action buttons */}
        <Box className={classes.actions}>
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
            onClick={handleShowAll}
            className={classes.actionButton}
          >
            {t('sharedAll')}
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
            onClick={handleHideAll}
            className={classes.actionButton}
          >
            {t('sharedHide')}
          </Button>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* Group list */}
        {sortedGroups.map((group) => (
          <Box key={group.id} className={classes.groupItem}>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={isGroupVisible(group.id)}
                  onChange={() => handleToggleGroup(group.id)}
                  className={classes.checkbox}
                />
              )}
              label={(
                <Box className={classes.groupLabel}>
                  <FolderIcon sx={{ fontSize: 16, color: group.color || 'action.active' }} />
                  <Typography className={classes.groupName}>
                    {group.name}
                  </Typography>
                </Box>
              )}
              sx={{ flex: 1, m: 0, ml: -0.5 }}
            />
            <Chip
              label={groupCounts[group.id] || 0}
              size="small"
              variant="outlined"
              className={classes.countChip}
            />
          </Box>
        ))}

        {/* Ungrouped POIs */}
        {groupCounts.ungrouped > 0 && (
          <Box className={classes.groupItem}>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={isGroupVisible('ungrouped')}
                  onChange={() => handleToggleGroup('ungrouped')}
                  className={classes.checkbox}
                />
              )}
              label={(
                <Box className={classes.groupLabel}>
                  <PlaceIcon sx={{ fontSize: 16 }} color="action" />
                  <Typography className={classes.groupName}>
                    {t('poiUngrouped') || 'Sem Grupo'}
                  </Typography>
                </Box>
              )}
              sx={{ flex: 1, m: 0, ml: -0.5 }}
            />
            <Chip
              label={groupCounts.ungrouped}
              size="small"
              variant="outlined"
              className={classes.countChip}
            />
          </Box>
        )}

        {/* Empty state */}
        {totalCount === 0 && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('poiNoData') || 'Nenhum POI cadastrado'}
            </Typography>
          </Box>
        )}
      </Box>
    </DraggableFilterPanel>
  );
};

export default PoiFilterPanel;
